import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import TransactionModal from "./TransactionModal";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ReimbursementModal({ open, onClose, selectedCashBook, onSuccess, relatedIOU, selectedIOU, editData = null   }) {

    const [claims, setClaims] = useState([]);
    const [selectedClaims, setSelectedClaims] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showTxModal, setShowTxModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState("");
    const token = sessionStorage.getItem("token");

    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const role = user.role || "";
    const isNormalUser = role === "Farm User" || role === "Outlet User";

    const [iouExpenses, setIouExpenses] = useState([]);
    const [manualExpenseTotal, setManualExpenseTotal] = useState(
        relatedIOU?.expense_total || 0
    );

    const [expenseList, setExpenseList] = useState(
        relatedIOU?.expense_items || []
    );

    const isIOUMode = Boolean(selectedIOU || relatedIOU);

    const totalAmount = relatedIOU
        ? Number(manualExpenseTotal || 0)
        : selectedClaims.reduce(
            (sum, c) => sum + c.reimbursed_amount,
            0
        );

    // 🔥 Fetch claims based on CashBook
    useEffect(() => {

        if (open) {

            fetchUsers();

            if (
                selectedCashBook ||
                isNormalUser
            ) {
                fetchClaims();
            }
        }

    }, [open, selectedCashBook, relatedIOU, selectedIOU]);

    useEffect(() => {

        if (
            isIOUMode &&
            selectedIOU?.requested_for_user
        ) {

            const userId =
                typeof selectedIOU.requested_for_user === "object"
                    ? selectedIOU.requested_for_user._id
                    : selectedIOU.requested_for_user;

            setSelectedUser(userId);
        }

    }, [selectedIOU, isIOUMode]);

    useEffect(() => {

        if (editData) {

            setSelectedUser(
                typeof editData.requested_for_user === "object"
                    ? editData.requested_for_user?._id
                    : editData.requested_for_user
            );

            if (editData.related_transactions?.length > 0) {

                const mapped =
                    editData.related_transactions.map(item => ({
                        transaction_id:
                            item.transaction_id?._id ||
                            item.transaction_id,

                        reimbursed_amount:
                        item.reimbursed_amount
                    }));

                setSelectedClaims(mapped);
            }
        }

    }, [editData]);

    const normalizeMoney = (value) => {

        const num = parseFloat(value);

        if (isNaN(num)) {
            return 0;
        }

        return num;
    };


    const fetchUsers = async () => {

        try {

            const res = await axios.get(
                `${API_BASE_URL}/pettycash/reimbursement-users`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const filteredUsers =
                (res.data.data || []).filter(
                    user =>
                        user.role === "Farm User" ||
                        user.role === "Outlet User"
                );

            setUsers(filteredUsers);

        } catch (err) {
            console.log(err);
        }
    };


    const fetchClaims = async () => {
        try {
            setLoading(true);

            let url = "";

            if (relatedIOU || selectedIOU) {

                const iouId = selectedIOU?._id || relatedIOU;
                url = `${API_BASE_URL}/pettycash/get-transactions?cash_book=${selectedCashBook._id}&related_iou=${iouId}`;
            } else {
                if (isNormalUser){
                    url = `${API_BASE_URL}/pettycash/get-transactions?transaction_type=CashInvoice&only_claimable=true&user=${user?._id}`;
                }else {
                    url = `${API_BASE_URL}/pettycash/get-transactions?cash_book=${selectedCashBook._id}&category=${selectedCashBook.category._id}&transaction_type=CashInvoice&only_claimable=true`;
                }
            }

            const res = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            let  transactions = res.data.data || [];

            if (isIOUMode) {

                const iouId =
                    selectedIOU?._id || relatedIOU;

                transactions = transactions.filter(tx => {

                    const relatedId =
                        typeof tx.related_iou === "object"
                            ? tx.related_iou?._id
                            : tx.related_iou;

                    return relatedId === iouId;
                });
            }

            setClaims(transactions);

            if (isIOUMode) {

                const iouTx = transactions.filter(
                    tx => tx.transaction_type === "IOUExpense"
                );

                setIouExpenses(iouTx);

                // ✅ IMPORTANT FIX
                setExpenseList(iouTx);

                const totalExpenses = iouTx.reduce(
                    (sum, tx) => sum + normalizeMoney(tx.amount),
                    0
                );

                setManualExpenseTotal(totalExpenses);

                const autoSelected = iouTx.map(tx => ({
                    transaction_id: tx._id,
                    reimbursed_amount: normalizeMoney(tx.amount)
                }));

                setSelectedClaims(autoSelected);
            } else {

                const autoSelected = transactions
                    .map(tx => {

                        const remaining =
                            tx.amount - (tx.reimbursed_amount || 0);

                        if (!isNormalUser && remaining <= 0) return null;

                        return {
                            transaction_id: tx._id,
                            reimbursed_amount: remaining
                        };
                    })
                    .filter(Boolean);

                setSelectedClaims(autoSelected);
            }

        } catch (err) {
            toast.error("Error loading claims");
        } finally {
            setLoading(false);
        }
    };

    // 🔥 Submit reimbursement
    const handleSubmit = async () => {

        if (
            relatedIOU &&
            totalAmount <= 0
        ) {
            return toast.error(
                "Add expenses first"
            );
        }

        if (
            !relatedIOU &&
            selectedClaims.length === 0
        ) {
            return toast.error(
                "No transactions selected"
            );
        }

        try {

            if (editData) {
                await axios.put(
                    `${API_BASE_URL}/pettycash/update-reimbursement/${editData._id}`,
                    {
                        requested_for_user: selectedUser,
                        related_transactions: selectedClaims
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
            } else {
                await axios.post(
                    `${API_BASE_URL}/pettycash/transaction`,
                    {
                        user_reimbursement: isNormalUser,
                        cash_book:
                            isNormalUser
                                ? null
                                : selectedCashBook?._id,

                        category:
                            isNormalUser
                                ? null
                                : selectedCashBook?.category?._id,

                        transaction_type: "Reimbursement",

                        requested_for_user:
                            isNormalUser
                                ? null
                                : (selectedUser || null),

                        related_iou:
                            relatedIOU || null,

                        related_transactions:
                            isIOUMode
                                ? expenseList.map(tx => ({
                                    transaction_id: tx._id,
                                    reimbursed_amount: normalizeMoney(tx.amount)
                                }))
                                : selectedClaims
                    },
                    {
                        headers: {
                            Authorization:
                                `Bearer ${token}`
                        }
                    }
                );
            }

            toast.success(
                editData
                    ? "Reimbursement updated"
                    : "Reimbursement submitted"
            );

            if (onSuccess) {
                onSuccess();
            }

            onClose();

        } catch (err) {

            toast.error(
                err.response?.data?.message ||
                "Error"
            );
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">

            <div className="bg-white rounded-2xl shadow-2xl w-[520px] max-w-[95vw] max-h-[90vh] flex flex-col overflow-hidden">

                {/* HEADER */}
                <div className="shrink-0 bg-green-700 text-white px-6 py-4 flex justify-between items-center">
                    <h2 className="text-lg font-semibold">
                        Add Reimbursement
                    </h2>

                    <button
                        onClick={onClose}
                        className="text-white text-xl font-bold"
                    >
                        ×
                    </button>
                </div>

                {/* BODY */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar scroll-smooth">

                    {/* CATEGORY (AUTO) */}
                    {
                        !isNormalUser && (
                            <div>
                                <label className="block mb-2 font-medium">
                                    Category
                                </label>

                                <input
                                    value={selectedCashBook?.category?.category_name || ""}
                                    readOnly
                                    className="w-full border px-4 py-2 rounded-lg bg-gray-100"
                                />
                            </div>
                        )
                    }

                    {/* CASHBOOK (AUTO) */}
                    {
                        !isNormalUser && (
                            <div>
                                <label className="block mb-2 font-medium">
                                    Cash Book
                                </label>

                                <input
                                    value={selectedCashBook?.cash_book_code || ""}
                                    readOnly
                                    className="w-full border px-4 py-2 rounded-lg bg-gray-100"
                                />
                            </div>
                        )
                    }

                    {
                        !isNormalUser && (
                            <div>
                                <label className="block mb-2 font-medium">
                                    {isIOUMode
                                        ? "Requested User"
                                        : "Select User"}
                                </label>

                                <select
                                    value={selectedUser}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    disabled={isIOUMode}
                                    className={`w-full border px-4 py-2 rounded-lg ${isIOUMode ? "bg-gray-100 cursor-not-allowed" : "" }`}
                                >
                                    <option value="">
                                        Select Farm / Outlet User
                                    </option>

                                    {
                                        users.map(user => (
                                            <option
                                                key={user._id}
                                                value={user._id}
                                            >
                                                {user.username} ({user.role})
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>
                        )
                    }

                    {/* TRANSACTIONS */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label className="font-medium">
                                Transactions
                            </label>

                            <button
                                onClick={() => {
                                    setSelectedTransaction(null);
                                    setShowTxModal(true)
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                            >
                                + Add Transaction
                            </button>
                        </div>

                        {
                            isIOUMode ? (
                                <>
                                    <div
                                        className="
                                                bg-blue-50
                                                border
                                                border-blue-200
                                                rounded-lg
                                                p-3
                                                text-sm
                                                text-blue-700
                                                mb-3
                                            "
                                    >
                                        Expenses added under this IOU
                                        will be automatically submitted
                                        for reimbursement.
                                    </div>

                                    {
                                        expenseList.length > 0 && (
                                            <div className="border rounded-lg p-3">

                                                <h4 className="font-semibold mb-2">
                                                    Added Expenses
                                                </h4>

                                                {
                                                    expenseList.map((tx, index) => (
                                                        <div
                                                            key={index}
                                                            onClick={() => {
                                                                setSelectedTransaction(tx);
                                                                setShowTxModal(true);
                                                            }}
                                                            className="
                                                                        flex
                                                                        justify-between
                                                                        text-sm
                                                                        border-b
                                                                        py-1
                                                                        cursor-pointer
                                                                    "
                                                        >
                                                            <span>
                                                                {tx.description}
                                                            </span>

                                                            <span className="text-red-600">
                                                                Rs {tx.amount}
                                                            </span>
                                                        </div>
                                                    ))
                                                }

                                            </div>
                                        )
                                    }
                                </>
                            ): (
                                <>
                                    {loading ? (
                                        <p className="text-gray-500">Loading...</p>
                                    ) : claims.length === 0 ? (
                                        <p className="text-gray-400 text-sm">
                                            No claimable transactions
                                        </p>
                                    ) : (
                                        claims.map(tx => {

                                            const remaining = tx.amount - (tx.reimbursed_amount || 0);

                                            if ( !isNormalUser && remaining <= 0) return null;

                                            return (
                                                <div key={tx._id}
                                                     onClick={() => {
                                                         setSelectedTransaction(tx);
                                                         setShowTxModal(true);
                                                     }}
                                                     className="border p-3 rounded mb-2 cursor-pointer hover:bg-gray-50">

                                                    <div className="flex justify-between items-center">

                                                        <div>
                                                            <p className="font-medium">
                                                                {tx.transaction_code}
                                                            </p>

                                                            <p className="text-sm text-gray-500">
                                                                Remaining: Rs {remaining}
                                                            </p>
                                                        </div>

                                                    </div>

                                                </div>
                                            );
                                        })
                                    )}
                                </>
                            )}


                    </div>

                    {
                        relatedIOU && selectedIOU && (

                            <div className=" bg-yellow-50 border p-4 rounded-lg mb-4">

                                <div className="
                                        flex
                                        justify-between
                                        mb-2
                                 ">
                                    <span className="font-medium">
                                        IOU Amount
                                    </span>

                                    <span>
                                        Rs {selectedIOU?.amount || 0}
                                    </span>

                                </div>

                                <div className="flex justify-between mb-2">
                                    <span className="font-medium">
                                        Total Expenses
                                    </span>

                                    <span className="text-red-600">
                                        Rs {totalAmount || 0}
                                    </span>
                                </div>

                                {(() => {

                                    const settlement =
                                        (selectedIOU?.amount || 0) -
                                        (totalAmount || 0);

                                    const isRemaining = settlement > 0;

                                    const isExtra = settlement < 0;

                                    const isExact = settlement === 0;

                                    return (
                                        <div className="flex justify-between font-bold border-t pt-2">
                                            <span>
                                                {
                                                    isRemaining
                                                        ? "Remaining Settlement"
                                                        : isExtra
                                                            ? "Extra Settlement"
                                                            : "Exact Match"
                                                }
                                            </span>

                                            <span className={
                                                    isRemaining
                                                        ? "text-green-700"
                                                        : isExtra
                                                            ? "text-red-600"
                                                            : "text-blue-600"
                                                }
                                            >
                                                    {
                                                        isExact
                                                        ? "Rs 0"
                                                        : isRemaining
                                                            ? `Rs ${settlement}`
                                                            : `- Rs ${Math.abs(settlement)}`
                                                    }
                                            </span>

                                        </div>
                                    );

                                })()}

                            </div>

                        )
                    }

                    {/* TOTAL */}
                    <div>
                        <label className="block mb-2 font-medium">
                            Total Amount
                        </label>

                        <input
                            type="number"
                            value={totalAmount}
                            readOnly
                            className="w-full border px-4 py-2 rounded-lg bg-gray-100"
                        />
                    </div>

                    {/* ACTIONS */}
                    <div className="shrink-0 flex justify-end gap-3  p-5  border-t bg-white">

                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-300 rounded-lg"
                        >
                            Cancel
                        </button>

                        <button
                            onClick={handleSubmit}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg"
                        >
                            Submit
                        </button>

                    </div>

                </div>
            </div>

            <TransactionModal
                open={showTxModal}
                onClose={() => {
                    setShowTxModal(false);
                    setSelectedTransaction(null);
                }}
                editData={selectedTransaction}
                selectedCashBook={selectedCashBook}
                relatedIOU={
                    selectedIOU?._id ||
                    relatedIOU ||
                    null
                }
                onSuccess={(data) => {

                    if (isIOUMode) {

                        const newTransactions = data?.transactions || [];

                        setExpenseList(prev => {

                            const updatedExpenses = [
                                ...prev,
                                ...newTransactions
                            ];

                            const updatedTotal = updatedExpenses.reduce(
                                (sum, tx) =>
                                    sum + normalizeMoney(tx.amount),
                                0
                            );

                            setManualExpenseTotal(updatedTotal);

                            setSelectedClaims(
                                updatedExpenses.map(tx => ({
                                    transaction_id: tx._id,
                                    reimbursed_amount: normalizeMoney(tx.amount)
                                }))
                            );

                            return updatedExpenses;
                        });

                    } else {

                        const newTransactions = data?.transactions || [];

                        if (data?.updated){

                            setClaims(prev =>
                                prev.map(tx =>
                                    tx._id === newTransactions[0]._id
                                        ? newTransactions[0]
                                        : tx
                                )
                            );

                            setSelectedClaims(prev =>
                                prev.map(item =>
                                    item.transaction_id === newTransactions[0]._id
                                        ? {
                                            ...item,
                                            reimbursed_amount:
                                                Number(newTransactions[0].amount || 0)
                                        }
                                        : item
                                )
                            )

                            return;
                        }

                        setClaims(prev => [
                            ...newTransactions,
                            ...prev
                        ]);

                        const mapped = newTransactions.map(tx => ({
                            transaction_id: tx._id,
                            reimbursed_amount: Number(tx.amount || 0)
                        }));

                        setSelectedClaims(prev => [
                            ...prev,
                            ...mapped
                        ]);

                        // if (onSuccess) onSuccess();
                    }
                }}
            />

        </div>
    );
}

export default ReimbursementModal;